import Lock from '@mui/icons-material/Lock';
import LockOpen from '@mui/icons-material/LockOpen';
import { Box, Card, Chip, FormControlLabel, Switch, Typography } from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { getBenefitIcon } from '../BenefitIcon';
import { useWellpassSettings } from '../wellpass/useWellpassSettings';
import { NonCashBenefit } from './const';

export interface NonCashBenefitConfig {
  isValidValue: (amount: number) => boolean;
}

interface FeatureCardProps {
  featureType: NonCashBenefit;
  config: NonCashBenefitConfig;
  isSelected: boolean;
  onToggle: (featureType: NonCashBenefit, enabled: boolean) => void;
  amount: number;
  disabled?: boolean;
}

export const NonCashBenefitCard: React.FC<FeatureCardProps> = ({ featureType, config, amount, isSelected, onToggle, disabled }) => {
  const { t } = useTranslation('benefitsModule');
  const { data: wellpassSettings, isLoading: isWellpassLoading } = useWellpassSettings();

  // While wellpassSettings is still loading, isValidValue optimistically returns true (so a pre-selected
  // WELLPASS isn't evicted). Treat WELLPASS as not-yet-available here so the card renders as disabled/locked
  // during the load instead of briefly flashing the "available" state, and only flips once loading completes.
  const isWellpassPending = featureType === NonCashBenefit.Wellpass && isWellpassLoading;
  const isAvailable = config.isValidValue(amount) && !isWellpassPending;
  const isToggleDisabled = !isAvailable || !!disabled;

  const Icon = getBenefitIcon(featureType);

  const fixedAmount =
    featureType === NonCashBenefit.Wellpass ? (wellpassSettings?.wellpassBudget ? wellpassSettings.wellpassBudget / 100 : 0) : amount;

  return (
    <Card
      sx={{
        opacity: isAvailable ? 1 : 0.6,
        py: 1,
        px: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              color: isAvailable ? 'primary.main' : 'text.secondary',
            }}
          >
            <Icon />
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  data-test-id={`toggle-${featureType}-feature`}
                  checked={isSelected}
                  onChange={e => onToggle(featureType, e.target.checked)}
                  disabled={isToggleDisabled}
                  color="primary"
                />
              }
              label={
                <>
                  <Typography>{t(`nonCashBenefits.${featureType}.name`)}</Typography>
                  <Typography variant="caption">{t(`nonCashBenefits.${featureType}.requirement`, { amount: fixedAmount })}</Typography>
                </>
              }
            />
          </Box>
        </Box>
        {isAvailable ? (
          <Chip icon={<LockOpen />} label={t('nonCashBenefits.available')} color="success" variant="outlined" size="small" />
        ) : (
          <Chip icon={<Lock />} label={t('nonCashBenefits.unavailable')} color="default" variant="outlined" size="small" />
        )}
      </Box>
    </Card>
  );
};
